import {
    createClusterFilters,
    createHealthStatFilters,
    createPresetOnChangeHandler,
    findAppliedFilters,
} from 'utils/presets';
import styled, { useTheme } from 'styled-components';
import { FiChevronDown } from 'react-icons/fi';
import FilterCheckboxChip from './FilterCheckboxChip';
import { IconButton } from 'components/Button';
import { MdSort } from 'react-icons/md';
import PresetTooltip from '../PresetTooltip';
import { TIKTOK_SORT_OPTIONS } from 'utils/metrics';
import { withMixpanelChange } from 'utils/app';

const [sustainedRiser, seasonalRiser, risingStar, noCluster] =
    createClusterFilters([
        'tiktok sustained riser',
        'tiktok seasonal riser',
        'tiktok rising star',
        'tiktok no cluster',
    ]);

const presets = {
    trendingThisWeekTikTok: {
        value: 'trendingThisWeekTikTok',
        label: 'Trending This Week',
        definition: 'Identify micro-viral sparks.',
        filters: {
            tikTokCluster: [risingStar, noCluster],
            healthStats: createHealthStatFilters([
                ['average weekly views', [0, 2, 4, 6]],
            ]),
        },
        sortOption: TIKTOK_SORT_OPTIONS.non_cum_increase_WoW,
    },
    trendingThisMonthTikTok: {
        value: 'trendingThisMonthTikTok',
        label: 'Trending This Month',
        definition: 'Pinpoint sustained cultural movements.',
        filters: {
            tikTokCluster: [sustainedRiser],
            healthStats: createHealthStatFilters([
                ['average weekly views', [0, 2, 4, 6]],
            ]),
        },
        sortOption: TIKTOK_SORT_OPTIONS.non_cum_increase_MoM_rolling,
    },
    organicTrendsTikTok: {
        value: 'organicTrendsTikTok',
        label: 'Organic Trends',
        definition:
            'Find trends growing through pure community energy rather than ad spend.',
        filters: {
            tikTokCluster: [sustainedRiser, seasonalRiser],
            healthStats: createHealthStatFilters([
                ['paid views', [8, 6]],
                ['posts', [4, 6, 8]],
            ]),
        },
        sortOption: TIKTOK_SORT_OPTIONS.non_cum_increase_MoM_rolling,
    },
    highEngagementTikTok: {
        value: 'highEngagementTikTok',
        label: 'High Engagement',
        definition: 'Discover niche hot topics.',
        filters: {
            tikTokCluster: [sustainedRiser, seasonalRiser],
            healthStats: createHealthStatFilters([
                ['posts', [2]],
                ['engagement', [8, 6, 4]],
            ]),
        },
        sortOption: TIKTOK_SORT_OPTIONS.non_cum_increase_MoM_rolling,
    },
    newTikTok: {
        value: 'newTikTok',
        label: 'New',
        definition: 'See what Spate has just added.',
        filters: {/** Must be applied on render */},
        sortOption: TIKTOK_SORT_OPTIONS.non_cum_avg_weekly_views,
    },
};

const PresetFilterCheckboxChipUntracked = ({ value, ...props }) => {
    return (
        <PresetTooltip preset={value} presets={presets}>
            <FilterCheckboxChip {...props} />
        </PresetTooltip>
    );
};

const PresetFilterCheckboxChipWithMixpanel = withMixpanelChange(
    PresetFilterCheckboxChipUntracked,
    'PresetFilterCheckboxChip',
);

export const PresetFilterCheckboxChip = ({
    mixpanelContext = 'PresetFilterSelect',
    ...props
}) => {
    return (
        <PresetFilterCheckboxChipWithMixpanel
            mixpanelContext={mixpanelContext}
            {...props}
        />
    );
};

const Wrapper = styled.div`
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.space_6}px;
`;

export function StarterTikTokPresetFilterChips({ setFilters, ...props }) {
    return (
        <Wrapper {...props}>
            <IconButton
                IconLeft={MdSort}
                IconRight={FiChevronDown}
                onClick={setFilters}
                css="align-self: flex-start;"
            >
                Filters
            </IconButton>
            {Object.values(presets).map(({ label, value }) => {
                return (
                    <PresetFilterCheckboxChip
                        value={value}
                        checked={false}
                        onChange={setFilters}
                        preserveIconSpace={false}
                        key={label}
                    >
                        <span>{label}</span>
                    </PresetFilterCheckboxChip>
                );
            })}
        </Wrapper>
    );
}

function createPresets(presets, week, year) {
    if (!(week && year)) return presets;
    return {
        ...presets,
        newTikTok: {
            ...presets.newTikTok,
            filters: {
                ...presets.newTikTok.filters,
                addedOn: [`${year}-${week}`],
            },
        },
    };
}

function TikTokPresetFilterChips({
    children,
    filters,
    setFilters,
    sortOption,
    setSortOption,
    initialSortOption,
    healthStatOptions,
    week,
    year,
    ...props
}) {
    const theme = useTheme();
    const renderPresets = createPresets(presets, week, year);
    const appliedFilters = findAppliedFilters({
        filters,
        presets: renderPresets,
        sortOption,
    });
    const onChange = createPresetOnChangeHandler({
        filters,
        setFilters,
        sortOption,
        setSortOption,
        initialSortOption,
        theme,
        presets: renderPresets,
    });
    return (
        <Wrapper {...props}>
            {children}
            {Object.values(renderPresets)
                .filter(({ filters }) => {
                    return (
                        !filters.healthStats ||
                        filters.healthStats.every(({ metricLabel }) =>
                            healthStatOptions.some(
                                ({ label }) => label === metricLabel,
                            ),
                        )
                    );
                })
                .map((option) => {
                    const { label, value } = option;
                    const checked = !!appliedFilters?.find(
                        (appliedFilter) => appliedFilter?.value === value,
                    );

                    return (
                        <PresetFilterCheckboxChip
                            value={value}
                            checked={checked}
                            onChange={(nextChecked) =>
                                onChange({
                                    action: nextChecked
                                        ? 'select-option'
                                        : 'remove-value',
                                    option,
                                })
                            }
                            handleMixpanelEvent={(nextChecked) => ({
                                action: nextChecked
                                    ? 'select-option'
                                    : 'remove-value',
                                label: option.label,
                                value: option.value,
                            })}
                            preserveIconSpace={false}
                            key={label}
                        >
                            <span>{label}</span>
                        </PresetFilterCheckboxChip>
                    );
                })}
        </Wrapper>
    );
}

export default TikTokPresetFilterChips;
